import React, { useState, useEffect } from "react";
import styles from "./EditJobModal.module.scss";
import classNames from "classnames/bind";
import { notify } from "~/utils/helper";
import {
  JobTypeOptions,
  LeadSourceOptions,
  ShingleOptions,
  CurrentStageOptions,
  PriorityOptions,
  PolicyTypeOptions,
  pitchValuePattern,
  squaresValuePattern,
} from "~/pages/admin/jobs/add-job/constants/add-job.constants";
import { ISalesRep } from "~/apis/userManagement";
import { ITradeType } from "~/apis/job";
import BaseModal from "~/components/common/base-modal/BaseModal";
import { EditV1Icon } from "~/assets/icons";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { IPayloadUpdateOverviewJobDetails } from "~/utils/interface/jobDetails";
import { isCustomerReferralLeadSource } from "~/utils/helpers/jobStage";

const cx = classNames.bind(styles);

interface EditJobModalProps {
  isOpen: boolean;
  jobDetail?: any;
  salesReps?: ISalesRep[];
  tradeTypes?: ITradeType[];
  onClose: () => void;
  onSave: (data: IPayloadUpdateOverviewJobDetails) => void;
}

const EditJobModal: React.FC<EditJobModalProps> = ({
  isOpen,
  jobDetail,
  salesReps,
  tradeTypes,
  onClose,
  onSave,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    jobNumber: "",
    jobName: "",
    jobType: "",
    priority: "",
    insuranceCompanyName: "",
    insuranceEmail: "",
    claimNumber: "",
    insurancePhoneNumber: "",
    adjusterName: "",
    policyType: "",
    salesRepId: "",
    paymentTerms: "",
    leadSource: "",
    referralName: "",
    tradeType: [] as string[],
    shingle: "",
    customShingleColor: "",
    shinglesManufacturer: "",
    numberOfSquares: "",
    currentStage: "",
    pitch: "",
    jobDescription: "",
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with jobDetail data
  useEffect(() => {
    if (jobDetail) {
      const savedColor = jobDetail.color || "";
      const isPresetShingle = ShingleOptions.some(
        (option) =>
          option.value === savedColor.toUpperCase() && option.value !== "CUSTOM"
      );
      setFormData({
        jobNumber: jobDetail.jobUniqueID || "",
        jobName: jobDetail.title || "",
        jobType: jobDetail.jobType || "",
        insuranceCompanyName: jobDetail.insuranceDetail?.companyName || "",
        insuranceEmail: jobDetail.insuranceDetail?.email || "",
        claimNumber: jobDetail.insuranceDetail?.claimNumber || "",
        insurancePhoneNumber: jobDetail.insuranceDetail?.phone || "",
        adjusterName: jobDetail.insuranceDetail?.adjusterName || "",
        policyType: jobDetail.insuranceDetail?.policyType || "",
        salesRepId: jobDetail.salesRep?.id != null ? String(jobDetail.salesRep.id) : "",
        priority: jobDetail.priority || "",
        paymentTerms: jobDetail.paymentTerms || "",
        leadSource: jobDetail.leadSource || "",
        referralName: jobDetail.referralName || "",
        tradeType: (jobDetail.tradeTypes || [])
          .map((trade: { id?: number | string }) =>
            trade?.id != null ? String(trade.id) : ""
          )
          .filter(Boolean),
        shingle: isPresetShingle
          ? savedColor.toUpperCase()
          : savedColor
          ? "CUSTOM"
          : "",
        customShingleColor: isPresetShingle || !savedColor ? "" : jobDetail.color,
        shinglesManufacturer: jobDetail.shingleManufacturer || "",
        numberOfSquares:
          jobDetail.numberOfSquares != null && jobDetail.numberOfSquares !== ""
            ? String(jobDetail.numberOfSquares)
            : "",
        currentStage: jobDetail.jobStage || "",
        pitch: jobDetail.pitch != null ? String(jobDetail.pitch) : "",
        jobDescription: jobDetail.description || "",
        notes: jobDetail.jobNote || "",
      });
      // Clear errors when initializing
      setErrors({});
    }
  }, [jobDetail]);

  const updateError = (field: string, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.jobNumber) {
      newErrors.jobNumber = "Job number is required";
    }
    if (!formData.jobName) {
      newErrors.jobName = "Job name is required";
    }
    if (!formData.jobType) {
      newErrors.jobType = "Job type is required";
    }
    if (!formData.salesRepId) {
      newErrors.salesRepId = "Sales rep is required";
    }
    if (!formData.leadSource) {
      newErrors.leadSource = "Lead source is required";
    }
    if (formData.tradeType.length === 0) {
      newErrors.tradeType = "Trade type is required";
    }
    if (
      formData.numberOfSquares &&
      !squaresValuePattern.test(String(formData.numberOfSquares))
    ) {
      newErrors.numberOfSquares = "# of Squares must be a number or fraction";
    }
    if (formData.pitch && !pitchValuePattern.test(formData.pitch)) {
      newErrors.pitch = "Pitch must be a number, 1/3, or 7-12";
    }
    if (
      formData.insuranceEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.insuranceEmail)
    ) {
      newErrors.insuranceEmail = "Email is invalid";
    }
    if (formData.claimNumber && !/^\d+$/.test(formData.claimNumber)) {
      newErrors.claimNumber = "Claim number must be numeric";
    }
    if (
      formData.insurancePhoneNumber &&
      !/^\d+$/.test(formData.insurancePhoneNumber)
    ) {
      newErrors.insurancePhoneNumber = "Phone number must be numeric";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (
    field: keyof typeof formData,
    value: string | string[]
  ) => {
    setFormData((prev) => {
      if (field === "tradeType") {
        return {
          ...prev,
          tradeType: Array.isArray(value) ? value : [],
        };
      }
      const text = typeof value === "string" ? value : "";
      const next = { ...prev, [field]: text };
      if (field === "leadSource" && !isCustomerReferralLeadSource(text)) {
        next.referralName = "";
      }
      if (field === "shingle" && text !== "CUSTOM") {
        next.customShingleColor = "";
      }
      return next;
    });

    if (errors[field]) {
      updateError(field, "");
    }

    if (field === "jobNumber" && !value) {
      updateError("jobNumber", "Job number is required");
    } else if (field === "jobName" && !value) {
      updateError("jobName", "Job name is required");
    } else if (field === "jobType" && !value) {
      updateError("jobType", "Job type is required");
    } else if (field === "salesRepId" && !value) {
      updateError("salesRepId", "Sales rep is required");
    } else if (field === "leadSource") {
      if (!value) {
        updateError("leadSource", "Lead source is required");
      }
      if (typeof value === "string" && !isCustomerReferralLeadSource(value)) {
        updateError("referralName", "");
      }
    } else if (
      field === "tradeType" &&
      (!Array.isArray(value) || value.length === 0)
    ) {
      updateError("tradeType", "Trade type is required");
    } else if (
      field === "numberOfSquares" &&
      typeof value === "string" &&
      value &&
      !squaresValuePattern.test(value)
    ) {
      updateError("numberOfSquares", "# of Squares must be a number or fraction");
    } else if (
      field === "pitch" &&
      typeof value === "string" &&
      value &&
      !pitchValuePattern.test(value)
    ) {
      updateError("pitch", "Pitch must be a number, 1/3, or 7-12");
    } else if (
      field === "insuranceEmail" &&
      typeof value === "string" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      updateError("insuranceEmail", "Email is invalid");
    } else if (
      field === "claimNumber" &&
      typeof value === "string" &&
      value &&
      !/^\d+$/.test(value)
    ) {
      updateError("claimNumber", "Claim number must be numeric");
    } else if (
      field === "insurancePhoneNumber" &&
      typeof value === "string" &&
      value &&
      !/^\d+$/.test(value)
    ) {
      updateError("insurancePhoneNumber", "Phone number must be numeric");
    }

    if (field === "jobType" && value !== "Insurance") {
      updateError("insuranceCompanyName", "");
      updateError("insuranceEmail", "");
      updateError("claimNumber", "");
      updateError("insurancePhoneNumber", "");
      updateError("policyType", "");
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSave = () => {
    if (!validateForm()) {
      notify("error", "Please fix all validation errors before saving");
      return;
    }

    const payload: IPayloadUpdateOverviewJobDetails = {
      jobTitle: formData.jobName,
      jobType: formData.jobType,
      salesRepId: parseInt(String(formData.salesRepId), 10),
      paymentTerms: formData.paymentTerms,
      leadSource: formData.leadSource,
      referralName: formData.referralName,
      tradeTypes: formData.tradeType
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id)),
      color:
        formData.shingle === "CUSTOM"
          ? formData.customShingleColor.trim()
          : formData.shingle,
      shingleManufacturer: formData.shinglesManufacturer,
      numberOfSquares: formData.numberOfSquares || null,
      ...(formData.currentStage ? { jobStage: formData.currentStage } : {}),
      pitch: formData.pitch,
      description: formData.jobDescription,
      jobNote: formData.notes,
      ...(formData.priority ? { priority: formData.priority } : {}),
      ...(formData.jobType === "Insurance"
        ? {
            insuranceCompanyName: formData.insuranceCompanyName,
            insuranceEmail: formData.insuranceEmail,
            insuranceClaimNumber: formData.claimNumber,
            insurancePhone: formData.insurancePhoneNumber,
            insuranceAdjusterName: formData.adjusterName,
            insurancePolicyType: formData.policyType,
          }
        : {}),
    };
    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <BaseModal open={isOpen} onClose={onClose} width="90vw" maxWidth="1210px">
      <div className={cx("edit-job-container")}>
        <div className={cx("header")}>
          <EditV1Icon className={cx("edit-icon")} />

          <h4 className={cx("header-title")}>Edit Job Details</h4>
        </div>

        <div className={cx("content-container")}>
          <div className={cx("top-section")}>
            <div className={cx("left-section")}>
              <div className={cx("row")}>
                <PrimaryInput
                  name="jobNumber"
                  label="Job Number"
                  placeholder="Enter job number"
                  value={formData.jobNumber}
                  onChange={(e) =>
                    handleFieldChange("jobNumber", e.target.value)
                  }
                  className={cx("input")}
                  error={!!errors.jobNumber}
                  errorMessage={errors.jobNumber}
                  disabled={true}
                />

                <PrimaryInput
                  name="jobName"
                  label="Job Name"
                  placeholder="Enter job name"
                  value={formData.jobName}
                  onChange={(e) => handleFieldChange("jobName", e.target.value)}
                  className={cx("input")}
                  error={!!errors.jobName}
                  errorMessage={errors.jobName}
                />
              </div>

              <div className={cx("row")}>
                <PrimarySelectInput
                  name="jobType"
                  label="Job Type"
                  placeholder="Select Job Type"
                  value={formData.jobType}
                  onChange={(value) => handleFieldChange("jobType", value)}
                  options={JobTypeOptions}
                  className={cx("select-input")}
                  errorMessage={errors.jobType}
                />

                <PrimarySelectInput
                  name="priority"
                  label="Priority"
                  placeholder="Select Priority"
                  value={
                    PriorityOptions.find(
                      (opt) => opt.value === formData.priority
                    )?.label || ""
                  }
                  onChange={(value) => handleFieldChange("priority", value)}
                  options={PriorityOptions}
                  className={cx("select-input")}
                />
              </div>

              {formData.jobType === "Insurance" && (
                <div className={cx("flex flex-col gap-4")}>
                  <div className={cx("row")}>
                    <PrimaryInput
                      name="insuranceCompanyName"
                      label="Insurance company Name"
                      placeholder="Enter Insurance company name"
                      value={formData.insuranceCompanyName}
                      onChange={(e) =>
                        handleFieldChange(
                          "insuranceCompanyName",
                          e.target.value
                        )
                      }
                      className={cx("input")}
                      error={!!errors.insuranceCompanyName}
                      errorMessage={errors.insuranceCompanyName}
                    />

                    <PrimaryInput
                      name="insuranceEmail"
                      label="Email"
                      placeholder="Enter email"
                      value={formData.insuranceEmail}
                      onChange={(e) =>
                        handleFieldChange("insuranceEmail", e.target.value)
                      }
                      className={cx("input")}
                      error={!!errors.insuranceEmail}
                      errorMessage={errors.insuranceEmail}
                    />
                  </div>

                  <div className={cx("row")}>
                    <PrimaryInput
                      name="claimNumber"
                      label="Claim Number"
                      placeholder="Enter claim number"
                      value={formData.claimNumber}
                      onChange={(e) =>
                        handleFieldChange("claimNumber", e.target.value)
                      }
                      className={cx("input")}
                      error={!!errors.claimNumber}
                      errorMessage={errors.claimNumber}
                    />

                    <PrimaryInput
                      name="insurancePhoneNumber"
                      label="Phone Number"
                      placeholder="Enter phone"
                      value={formData.insurancePhoneNumber}
                      onChange={(e) =>
                        handleFieldChange(
                          "insurancePhoneNumber",
                          e.target.value
                        )
                      }
                      className={cx("input")}
                      error={!!errors.insurancePhoneNumber}
                      errorMessage={errors.insurancePhoneNumber}
                    />
                  </div>

                  <div className={cx("row")}>
                    <PrimaryInput
                      name="adjusterName"
                      label="Adjuster Name (Optional)"
                      placeholder="Enter adjuster name"
                      value={formData.adjusterName}
                      onChange={(e) =>
                        handleFieldChange("adjusterName", e.target.value)
                      }
                      className={cx("input")}
                    />

                    <PrimarySelectInput
                      name="policyType"
                      label="Policy Type"
                      placeholder="Select policy"
                      options={PolicyTypeOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                      value={
                        PolicyTypeOptions.find(
                          (opt) => opt.value === formData.policyType
                        )?.label || ""
                      }
                      onChange={(value) =>
                        handleFieldChange("policyType", value)
                      }
                      className={cx("select-input")}
                      errorMessage={errors.policyType}
                    />
                  </div>
                </div>
              )}

              <div className={cx("row")}>
                <PrimarySelectInput
                  name="salesRepId"
                  label="Sales Rep"
                  placeholder="Select Sales Rep"
                  value={
                    salesReps?.find(
                      (rep) => String(rep.id) === String(formData.salesRepId)
                    )?.username || ""
                  }
                  onChange={(value) =>
                    handleFieldChange("salesRepId", String(value))
                  }
                  options={salesReps?.map((rep) => ({
                    label: rep.username,
                    value: String(rep.id),
                  }))}
                  className={cx("select-input")}
                  errorMessage={errors.salesRepId}
                />
              </div>

              <div className={cx("row")}>
                <PrimaryInput
                  name="paymentTerms"
                  label="Payment Terms"
                  placeholder="Enter payment terms"
                  value={formData.paymentTerms}
                  onChange={(e) =>
                    handleFieldChange("paymentTerms", e.target.value)
                  }
                  className={cx("input")}
                  // error={!!errors.paymentTerms}
                />
              </div>
            </div>

            <div className={cx("right-section")}>
              <h3 className={cx("section-title")}>Job Details</h3>

              <div className={cx("row")}>
                <PrimarySelectInput
                  name="leadSource"
                  label="Lead Source"
                  placeholder="Select source"
                  options={LeadSourceOptions}
                  value={formData.leadSource}
                  onChange={(value) => handleFieldChange("leadSource", value)}
                  className={cx("select-input")}
                  errorMessage={errors.leadSource}
                />

                <PrimaryInput
                  name="referralName"
                  label="Name of the Referral (Optional)"
                  placeholder="Enter name"
                  value={formData.referralName}
                  onChange={(e) =>
                    handleFieldChange("referralName", e.target.value)
                  }
                  className={cx("input")}
                  error={!!errors.referralName}
                  errorMessage={errors.referralName}
                />
              </div>

              <div className={cx("row")}>
                <PrimarySelectInput
                  name="tradeType"
                  label="Trade Type"
                  placeholder="Select Trade Type"
                  multiple
                  options={tradeTypes?.map((option) => ({
                    label: option.name,
                    value: String(option.id),
                  }))}
                  value={formData.tradeType}
                  onChange={(value) =>
                    handleFieldChange(
                      "tradeType",
                      Array.isArray(value) ? value.map(String) : []
                    )
                  }
                  className={cx("select-input")}
                  errorMessage={errors.tradeType}
                />

                <PrimarySelectInput
                  name="shingle"
                  label="Shingle colors (Optional)"
                  placeholder="Select color"
                  options={ShingleOptions?.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  value={
                    ShingleOptions.find((opt) => opt.value === formData.shingle)
                      ?.label || ""
                  }
                  onChange={(value) => handleFieldChange("shingle", value)}
                  className={cx("select-input")}
                  errorMessage={errors.shingle}
                />
              </div>

              {formData.shingle === "CUSTOM" && (
                <div className={cx("row")}>
                  <PrimaryInput
                    name="customShingleColor"
                    label="Custom Color (Optional)"
                    placeholder="Enter custom color"
                    value={formData.customShingleColor}
                    onChange={(e) =>
                      handleFieldChange("customShingleColor", e.target.value)
                    }
                    className={cx("input")}
                    error={!!errors.customShingleColor}
                    errorMessage={errors.customShingleColor}
                  />
                </div>
              )}

              <div className={cx("row")}>
                <PrimaryInput
                  name="shinglesManufacturer"
                  label="Shingles Manufacturer (Optional)"
                  placeholder="Add Name"
                  value={formData.shinglesManufacturer}
                  onChange={(e) =>
                    handleFieldChange("shinglesManufacturer", e.target.value)
                  }
                  className={cx("input")}
                  error={!!errors.shinglesManufacturer}
                  errorMessage={errors.shinglesManufacturer}
                />

                <PrimaryInput
                  name="numberOfSquares"
                  label="# of Squares (Optional)"
                  placeholder="Squares"
                  value={formData.numberOfSquares}
                  onChange={(e) =>
                    handleFieldChange("numberOfSquares", e.target.value)
                  }
                  className={cx("input")}
                  error={!!errors.numberOfSquares}
                  errorMessage={errors.numberOfSquares}
                />
              </div>

              <div className={cx("row")}>
                <PrimarySelectInput
                  name="currentStage"
                  label="Current Stage (Optional)"
                  placeholder="Select Stage"
                  options={CurrentStageOptions.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  value={
                    CurrentStageOptions.find(
                      (opt) => opt.value === formData.currentStage
                    )?.label || ""
                  }
                  onChange={(value) => handleFieldChange("currentStage", value)}
                  className={cx("select-input")}
                  errorMessage={errors.currentStage}
                />

                <PrimaryInput
                  name="pitch"
                  label="Pitch (Optional)"
                  placeholder="Pitch"
                  value={formData.pitch}
                  onChange={(e) => handleFieldChange("pitch", e.target.value)}
                  className={cx("input")}
                  error={!!errors.pitch}
                  errorMessage={errors.pitch}
                />
              </div>
            </div>
          </div>
          <div className={cx("section", "px-[34px]")}>
            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Job Description</h4>
              <textarea
                className={cx("textarea", "h-[80px]")}
                placeholder="Enter job description"
                value={formData.jobDescription}
                onChange={(e) =>
                  handleFieldChange("jobDescription", e.target.value)
                }
                rows={3}
              />
            </div>

            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Notes</h4>
              <textarea
                className={cx("textarea", "h-[110px]")}
                placeholder="Enter notes"
                value={formData.notes}
                onChange={(e) => handleFieldChange("notes", e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className={cx("bottom-section")}>
          <PrimaryButton
            title="Cancel"
            onClick={handleCancel}
            className={cx("cancel-button")}
          />
          <PrimaryButton
            title="Save"
            onClick={handleSave}
            className={cx("save-button")}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default EditJobModal;
